import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import { CreateDepartmentDto, DepartmentResponse, Department, DepartmentUser, DepartmentTreeNodeDTO } from '../interfaces/department.interface';

export const DepartmentService = {
    getDepartmentsByInstitution: async (): Promise<DepartmentResponse> => {
        const institutionId = AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.DEPARTMENT.BY_INSTITUTION(institutionId), {
            params: { page: 0, size: 1000 },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    /**
     * Location picker for the maintenance screens: the member-level lookup, because the department list above needs
     * DEPARTMENT_MANAGE and returned 403 (an empty Location filter) for a maintenance manager or technician.
     */
    getLocationOptions: async (): Promise<DepartmentResponse> => {
        const institutionId = AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.MAINTENANCE.MY_BREAKDOWN_LOCATIONS(institutionId), {
            headers: AuthService.getAuthHeader()
        });
        const rows = (response.data ?? []) as Array<{ id: string; name: string; unitType: Department['unitType']; parentId: string | null }>;
        const content = rows.map(r => ({ id: r.id, name: r.name, unitType: r.unitType, parentId: r.parentId, institutionId, code: null, sortOrder: 0 }));
        return { content } as unknown as DepartmentResponse;
    },

    getDepartmentTree: async (institutionId: string): Promise<DepartmentTreeNodeDTO[]> => {
        const response = await axios.get(ENDPOINTS.DEPARTMENT.TREE(institutionId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getDepartmentById: async (id: string): Promise<Department> => {
        const response = await axios.get(ENDPOINTS.DEPARTMENT.BY_ID(id), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    createDepartment: async (dto: CreateDepartmentDto): Promise<Department> => {
        dto.institutionId = AuthService.getSelectedInstitution()!!;
        const response = await axios.post(ENDPOINTS.DEPARTMENT.BASE, dto, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    updateDepartment: async (id: string, dto: CreateDepartmentDto): Promise<Department> => {
        dto.institutionId = AuthService.getSelectedInstitution()!!;
        const response = await axios.put(ENDPOINTS.DEPARTMENT.UPDATE(id), dto, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    deleteDepartment: async (id: string): Promise<void> => {
        await axios.delete(ENDPOINTS.DEPARTMENT.DELETE(id), {
            headers: AuthService.getAuthHeader()
        });
    },

    getDepartmentUsers: async (departmentId: string): Promise<DepartmentUser[]> => {
        const response = await axios.get(ENDPOINTS.DEPARTMENT.USERS(departmentId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    updateDepartmentUsers: async (departmentId: string, userIds: string[]): Promise<DepartmentUser[]> => {
        const response = await axios.put(ENDPOINTS.DEPARTMENT.USERS(departmentId), { userIds }, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    toggleDepartmentAdmin: async (departmentId: string, userId: string): Promise<DepartmentUser> => {
        const response = await axios.put(ENDPOINTS.DEPARTMENT.TOGGLE_ADMIN(departmentId, userId), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },
};
